"use client";

import {JSX, ReactNode, useRef} from "react";

import {type AdvancedFiltersInstance} from "@/features/rental";

import {useDetectClickOutside, useMatchMedia} from "@/shared/hooks";
import {FloatingDropdown} from "@/shared/ui/overlay/floating-dropdown";
import {Modal} from "@/shared/ui/overlay/modal";

import {AdvancedFiltersActions, AdvancedFiltersView} from "./advanced-filters.view";

import "./styles/advanced-filters-responsive.scss";

interface AdvancedFiltersResponsiveProps {
    model: AdvancedFiltersInstance;
    isOpen: boolean;
    onClose: () => void;
    trigger: ReactNode;
    breakpoint?: number;
    placement?: "bottom-start" | "bottom-end" | "right-start" | "right-end" | "left-start" | "left-end";
}

export function AdvancedFiltersResponsive({
    model,
    isOpen,
    onClose,
    trigger,
    breakpoint = 768,
    placement = "bottom-end",
}: AdvancedFiltersResponsiveProps): JSX.Element {
    const isMobile = useMatchMedia({maxWidth: breakpoint});
    const triggerRef = useRef<HTMLDivElement>(null);

    const handleUpdate = (): void => {
        model.updateFilter();
        onClose();
    };

    // Detect click outside to close dropdown (desktop only)
    const outsideClickRef = useDetectClickOutside<HTMLDivElement>({
        onTriggered: () => {
            if (isOpen && !isMobile) {
                handleUpdate();
            }
        },
        ignoredRefs: [triggerRef],
    });

    const handleClear = (): void => {
        model.clear();
    };

    if (isMobile) {
        return (
            <>
                {trigger}
                <Modal
                    isOpen={isOpen}
                    onClose={onClose}
                    header={<h2 className="advanced-filters-modal__title">Filters</h2>}
                    footer={<AdvancedFiltersActions onClear={handleClear} onUpdate={handleUpdate} buttonSize="md" />}
                >
                    <AdvancedFiltersView model={model} onUpdate={handleUpdate} onClose={onClose} showActions={false} />
                </Modal>
            </>
        );
    }

    // Desktop - FloatingDropdown with outside click detection
    return (
        <FloatingDropdown
            isOpen={isOpen}
            onOpenChange={(open) => !open && onClose()}
            placement={placement}
            dropdownClassName="advanced-filters-dropdown"
            trigger={<div ref={triggerRef}>{trigger}</div>}
        >
            <div ref={outsideClickRef}>
                <AdvancedFiltersView model={model} onUpdate={handleUpdate} onClose={onClose} showActions={true} />
            </div>
        </FloatingDropdown>
    );
}
